import {
  ensureTelegramWebAppScript,
  getTelegramLanguageCode,
  getTelegramLaunchUser,
  getTelegramStartParam,
  isTelegramMiniApp,
} from "./telegramAuth";
import {
  getInternationalHostHint,
  initializeInternationalHost,
  isInternationalHost,
  type InternationalHost,
} from "./internationalHost";

export type HostPlatform =
  | "web"
  | "telegram"
  | "vk"
  | "max"
  | "line"
  | "discord"
  | "teams"
  | "unknown";

export type HostUser = {
  id?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
};

export type HostShareData = {
  title?: string;
  text?: string;
  url?: string;
};

export type HostHapticStyle = "light" | "medium" | "heavy" | "rigid" | "soft";

export type PlatformCapabilities = {
  /** The wrapper draws top/back chrome even when it exposes no callback API. */
  ownsTopBackChrome: boolean;
  canRegisterBackHandler: boolean;
  providesViewport: boolean;
  showProductNavigation: boolean;
};

type HostBackButton = {
  show?: () => void;
  hide?: () => void;
  onClick?: (callback: () => void) => void;
  offClick?: (callback: () => void) => void;
};

type HostInsets = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

type MaxWebApp = {
  initData?: string;
  initDataUnsafe?: {
    user?: {
      id?: string | number;
      first_name?: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
      language_code?: string;
    };
    start_param?: string;
  };
  platform?: string;
  /** True once the client's Bot API version is >= the given one, e.g. "8.0". Telegram only. */
  isVersionAtLeast?: (version: string) => boolean;
  ready?: () => void;
  expand?: () => void;
  openLink?: (url: string) => void;
  shareURL?: (url: string, text?: string) => void;
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
  viewportHeight?: number;
  viewportStableHeight?: number;
  safeAreaInset?: HostInsets;
  contentSafeAreaInset?: HostInsets;
  onEvent?: (event: string, callback: () => void) => void;
  offEvent?: (event: string, callback: () => void) => void;
  BackButton?: HostBackButton;
  HapticFeedback?: {
    impactOccurred?: (style: HostHapticStyle) => void;
  };
};

type VkBridge = {
  send: (method: string, params?: Record<string, unknown>) => Promise<unknown>;
};

declare global {
  interface Window {
    /** Injected by VK Mini Apps. It is a host bridge, not an auth proof. */
    vkBridge?: VkBridge;
    /** Injected by MAX Mini Apps. Kept separate from Telegram.WebApp. */
    WebApp?: MaxWebApp;
  }
}

export interface PlatformAdapter {
  readonly platform: HostPlatform;
  readonly isEmbedded: boolean;
  readonly hasNativeBackButton: boolean;
  readonly capabilities: PlatformCapabilities;
  ready(): Promise<void>;
  expand(): Promise<void>;
  getLaunchData(): string | null;
  getStartParam(): string | null;
  getUser(): HostUser | null;
  getLanguage(): string | null;
  showBackButton(callback: () => void): () => void;
  setClosingConfirmation(enabled: boolean): void;
  syncViewportCssVars(): () => void;
  openLink(url: string): void;
  share(data: HostShareData): Promise<void>;
  haptic(style?: HostHapticStyle): void;
}

const CSS_VAR_NAMES = {
  height: "--host-viewport-height",
  stableHeight: "--host-viewport-stable-height",
  safeTop: "--host-safe-area-inset-top",
  safeRight: "--host-safe-area-inset-right",
  safeBottom: "--host-safe-area-inset-bottom",
  safeLeft: "--host-safe-area-inset-left",
  contentTop: "--host-content-safe-area-inset-top",
  contentRight: "--host-content-safe-area-inset-right",
  contentBottom: "--host-content-safe-area-inset-bottom",
  contentLeft: "--host-content-safe-area-inset-left",
} as const;

function getUrl(): URL | null {
  if (typeof window === "undefined") return null;
  try {
    return new URL(window.location.href);
  } catch {
    return null;
  }
}

function hasTelegramLaunchParams(): boolean {
  const url = getUrl();
  return Boolean(
    url &&
      (/tgWebAppData=/.test(url.hash) ||
        url.searchParams.has("tgWebAppData") ||
        url.searchParams.has("tgWebAppVersion")),
  );
}

function hasVkLaunchParams(): boolean {
  const url = getUrl();
  return Boolean(url?.searchParams.has("vk_app_id") || url?.searchParams.has("vk_user_id"));
}

function getHashParams(): URLSearchParams {
  const url = getUrl();
  return new URLSearchParams(url?.hash.replace(/^#/, "") ?? "");
}

function hasMaxLaunchParams(): boolean {
  const params = getHashParams();
  return (
    params.has("WebAppData") ||
    params.has("WebAppPlatform") ||
    params.has("WebAppVersion")
  );
}

function configuredInternationalHost(): InternationalHost | null {
  const configured = String(import.meta.env.VITE_HOST_PLATFORM ?? "").toLowerCase();
  return isInternationalHost(configured) ? configured : null;
}

function hasLineLaunchParams(): boolean {
  const url = getUrl();
  return Boolean(url?.searchParams.has("liff.state") || url?.searchParams.has("liffClientId"));
}

function hasDiscordLaunchParams(): boolean {
  const url = getUrl();
  return Boolean(
    url?.searchParams.has("frame_id") &&
      (url.searchParams.has("instance_id") || url.searchParams.has("channel_id")),
  );
}

function hasTeamsLaunchParams(): boolean {
  const url = getUrl();
  if (!url) return false;
  return Boolean(
    url.searchParams.has("hostClientType") ||
      url.searchParams.has("teamsAppId") ||
      url.searchParams.has("teamsContext") ||
      document.referrer.includes("teams.microsoft.com"),
  );
}

/** Host detection never treats launch data as authentication. */
export function detectPlatform(): HostPlatform {
  if (typeof window === "undefined") return "web";
  if (isTelegramMiniApp() || hasTelegramLaunchParams()) return "telegram";
  if (window.vkBridge || hasVkLaunchParams()) return "vk";
  if (window.WebApp || hasMaxLaunchParams()) return "max";
  if (hasLineLaunchParams()) return "line";
  const configuredHost = configuredInternationalHost();
  if (configuredHost) return configuredHost;
  if (hasTeamsLaunchParams()) return "teams";
  if (hasDiscordLaunchParams()) return "discord";
  if (window.parent !== window) return "unknown";
  return "web";
}

function currentUrlStartParam(keys: string[]): string | null {
  const url = getUrl();
  if (!url) return null;
  for (const key of keys) {
    const value = url.searchParams.get(key);
    if (value) return value;
  }
  return null;
}

function currentHashStartParam(keys: string[]): string | null {
  const params = getHashParams();
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return null;
}

function browserLanguage(): string | null {
  return typeof navigator === "undefined" ? null : navigator.language || null;
}

function normalizeUser(
  user:
    | {
        id?: string | number;
        first_name?: string;
        last_name?: string;
        username?: string;
        photo_url?: string;
        photo_200?: string;
      }
    | null
    | undefined,
): HostUser | null {
  if (!user) return null;
  return {
    id: user.id === undefined ? undefined : String(user.id),
    firstName: user.first_name,
    lastName: user.last_name,
    username: user.username,
    photoUrl: user.photo_url ?? user.photo_200,
  };
}

function setPx(name: string, value: number | undefined): void {
  if (typeof document === "undefined") return;
  // A reported 0 is indistinguishable from "not reported" for these insets,
  // so keep the CSS custom property unset rather than write a literal 0px —
  // that would otherwise clobber the `env(safe-area-inset-*, 0px)` fallback
  // chain in globals.css with a value the host never actually measured.
  if (!Number.isFinite(value) || value === 0) {
    document.documentElement.style.removeProperty(name);
    return;
  }
  document.documentElement.style.setProperty(name, `${value}px`);
}

/**
 * Mobile Telegram draws its own header — the back/close control and the
 * menu — over the page. Its bridge always creates `contentSafeAreaInset`,
 * but clients older than Bot API 8.0 report zeros in it instead of omitting
 * it, so the object's mere presence can't tell us whether the height was
 * actually measured. We only trust it once the client confirms Bot API 8.0
 * via `isVersionAtLeast`; otherwise we fall back to a measured floor.
 *
 * 80px is what the header plus status bar measured on the Android client
 * that reported the overlap in GW2DO; the device inset still wins where it
 * is larger. Desktop and web clients put their header above the web view
 * rather than over it, so they are left alone. MAX's bridge doesn't share
 * this quirk, so the floor only applies to Telegram.
 */
const OVERLAY_HEADER_TOP_PX = 80;
const OVERLAY_HEADER_PLATFORMS = new Set(["android", "android_x", "ios"]);

function contentInsets(
  host: MaxWebApp | undefined,
  options?: { isTelegram?: boolean },
): HostInsets | undefined {
  if (!host) return undefined;
  const safe = host.safeAreaInset;
  const content = host.contentSafeAreaInset;
  // `contentSafeAreaInset` is measured from the edge of `safeAreaInset`: in
  // fullscreen mode the status bar sits in the first and the header controls
  // in the second, so the page has to clear their sum, not the larger one.
  const merged: HostInsets | undefined = content
    ? {
        top: (content.top ?? 0) + (safe?.top ?? 0),
        right: (content.right ?? 0) + (safe?.right ?? 0),
        bottom: (content.bottom ?? 0) + (safe?.bottom ?? 0),
        left: (content.left ?? 0) + (safe?.left ?? 0),
      }
    : safe;
  const confirmedBotApi8 = host.isVersionAtLeast?.("8.0") === true;
  const needsFloor =
    options?.isTelegram &&
    !confirmedBotApi8 &&
    OVERLAY_HEADER_PLATFORMS.has(host.platform ?? "");
  if (!needsFloor) return merged;
  return { ...merged, top: Math.max(merged?.top ?? 0, OVERLAY_HEADER_TOP_PX) };
}

function setInsets(prefix: "safe" | "content", insets: HostInsets | undefined): void {
  setPx(CSS_VAR_NAMES[`${prefix}Top`], insets?.top);
  setPx(CSS_VAR_NAMES[`${prefix}Right`], insets?.right);
  setPx(CSS_VAR_NAMES[`${prefix}Bottom`], insets?.bottom);
  setPx(CSS_VAR_NAMES[`${prefix}Left`], insets?.left);
}

function syncBrowserViewport(): void {
  if (typeof window === "undefined") return;
  const height = window.visualViewport?.height ?? window.innerHeight;
  setPx(CSS_VAR_NAMES.height, height);
  setPx(CSS_VAR_NAMES.stableHeight, height);
}

function makeViewportSync(
  readHost?: () => MaxWebApp | undefined,
  options?: { isTelegram?: boolean },
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const sync = () => {
    const host = readHost?.();
    const fallbackHeight = window.visualViewport?.height ?? window.innerHeight;
    setPx(CSS_VAR_NAMES.height, host?.viewportHeight ?? fallbackHeight);
    setPx(CSS_VAR_NAMES.stableHeight, host?.viewportStableHeight ?? fallbackHeight);
    setInsets("safe", host?.safeAreaInset);
    setInsets("content", contentInsets(host, options));
  };
  sync();
  window.addEventListener("resize", sync);
  window.visualViewport?.addEventListener("resize", sync);
  const host = readHost?.();
  const hostEvents = ["viewportChanged", "safeAreaChanged", "contentSafeAreaChanged"];
  for (const event of hostEvents) host?.onEvent?.(event, sync);
  return () => {
    window.removeEventListener("resize", sync);
    window.visualViewport?.removeEventListener("resize", sync);
    for (const event of hostEvents) host?.offEvent?.(event, sync);
  };
}

function fallbackOpenLink(url: string): void {
  if (typeof window === "undefined") return;
  window.open(url, "_blank", "noopener,noreferrer");
}

async function fallbackShare(data: HostShareData): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.share) {
    await navigator.share(data);
    return;
  }
  if (data.url) fallbackOpenLink(data.url);
}

function noopBackButton(): () => void {
  return () => undefined;
}

function createWebAdapter(platform: "web" | "unknown" = "web"): PlatformAdapter {
  return {
    platform,
    isEmbedded: platform === "unknown",
    hasNativeBackButton: false,
    capabilities: {
      ownsTopBackChrome: false,
      canRegisterBackHandler: false,
      providesViewport: false,
      showProductNavigation: true,
    },
    ready: async () => undefined,
    expand: async () => undefined,
    getLaunchData: () => null,
    getStartParam: () => currentUrlStartParam(["start_param", "startapp"]),
    getUser: () => null,
    getLanguage: browserLanguage,
    showBackButton: noopBackButton,
    setClosingConfirmation: () => undefined,
    syncViewportCssVars: () => makeViewportSync(),
    openLink: fallbackOpenLink,
    share: fallbackShare,
    haptic: () => {
      if (typeof navigator !== "undefined") navigator.vibrate?.(10);
    },
  };
}

function createTelegramAdapter(): PlatformAdapter {
  const getWebApp = () => window.Telegram?.WebApp as MaxWebApp | undefined;
  return {
    platform: "telegram",
    isEmbedded: true,
    get hasNativeBackButton() {
      return Boolean(getWebApp()?.BackButton);
    },
    get capabilities() {
      return {
        ownsTopBackChrome: true,
        canRegisterBackHandler: Boolean(getWebApp()?.BackButton),
        providesViewport: true,
        showProductNavigation: true,
      };
    },
    ready: async () => {
      await ensureTelegramWebAppScript();
      getWebApp()?.ready?.();
    },
    expand: async () => {
      await ensureTelegramWebAppScript();
      getWebApp()?.expand?.();
    },
    getLaunchData: () => getWebApp()?.initData || null,
    getStartParam: getTelegramStartParam,
    getUser: () => {
      const user = getTelegramLaunchUser();
      return user
        ? {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            username: user.username,
            photoUrl: user.photoUrl,
          }
        : null;
    },
    getLanguage: getTelegramLanguageCode,
    showBackButton: (callback) => {
      const button = getWebApp()?.BackButton;
      if (!button) return noopBackButton();
      button.show?.();
      button.onClick?.(callback);
      return () => {
        button.offClick?.(callback);
        button.hide?.();
      };
    },
    setClosingConfirmation: (enabled) => {
      const webApp = getWebApp();
      if (enabled) webApp?.enableClosingConfirmation?.();
      else webApp?.disableClosingConfirmation?.();
    },
    syncViewportCssVars: () => makeViewportSync(getWebApp, { isTelegram: true }),
    openLink: (url) => {
      const webApp = getWebApp();
      if (webApp?.openLink) webApp.openLink(url);
      else fallbackOpenLink(url);
    },
    share: fallbackShare,
    haptic: (style = "light") => getWebApp()?.HapticFeedback?.impactOccurred?.(style),
  };
}

function createMaxAdapter(): PlatformAdapter {
  const getWebApp = () => window.WebApp;
  return {
    platform: "max",
    isEmbedded: true,
    get hasNativeBackButton() {
      return Boolean(getWebApp()?.BackButton);
    },
    get capabilities() {
      return {
        ownsTopBackChrome: true,
        canRegisterBackHandler: Boolean(getWebApp()?.BackButton),
        providesViewport: true,
        showProductNavigation: true,
      };
    },
    ready: async () => getWebApp()?.ready?.(),
    expand: async () => getWebApp()?.expand?.(),
    getLaunchData: () => getWebApp()?.initData || getHashParams().get("WebAppData"),
    getStartParam: () =>
      getWebApp()?.initDataUnsafe?.start_param ??
      currentHashStartParam(["WebAppStartParam", "start_param", "startapp"]) ??
      currentUrlStartParam(["start_param", "startapp"]),
    getUser: () => normalizeUser(getWebApp()?.initDataUnsafe?.user),
    getLanguage: () =>
      getWebApp()?.initDataUnsafe?.user?.language_code ?? browserLanguage(),
    showBackButton: (callback) => {
      const button = getWebApp()?.BackButton;
      if (!button) return noopBackButton();
      button.show?.();
      button.onClick?.(callback);
      return () => {
        button.offClick?.(callback);
        button.hide?.();
      };
    },
    setClosingConfirmation: (enabled) => {
      if (enabled) getWebApp()?.enableClosingConfirmation?.();
      else getWebApp()?.disableClosingConfirmation?.();
    },
    syncViewportCssVars: () => makeViewportSync(getWebApp),
    openLink: (url) => {
      const webApp = getWebApp();
      if (webApp?.openLink) webApp.openLink(url);
      else fallbackOpenLink(url);
    },
    share: async (data) => {
      if (data.url && getWebApp()?.shareURL) {
        getWebApp()?.shareURL?.(data.url, data.text);
        return;
      }
      await fallbackShare(data);
    },
    haptic: (style = "light") => getWebApp()?.HapticFeedback?.impactOccurred?.(style),
  };
}

function createVkAdapter(): PlatformAdapter {
  let user: HostUser | null = null;
  const send = async (method: string, params?: Record<string, unknown>) =>
    window.vkBridge?.send(method, params);

  return {
    platform: "vk",
    isEmbedded: true,
    hasNativeBackButton: false,
    capabilities: {
      ownsTopBackChrome: true,
      canRegisterBackHandler: false,
      providesViewport: false,
      showProductNavigation: true,
    },
    ready: async () => {
      if (!(await ensureVkBridge())) return;
      try {
        await send("VKWebAppInit");
      } catch {
        return;
      }
      try {
        const response = (await send("VKWebAppGetUserInfo")) as
          | {
              id?: number;
              first_name?: string;
              last_name?: string;
              photo_200?: string;
            }
          | undefined;
        user = normalizeUser(response);
      } catch {
        // User hints improve onboarding, but are never required for auth/UI boot.
      }
    },
    expand: async () => undefined,
    getLaunchData: () => getUrl()?.searchParams.toString() || null,
    getStartParam: () => currentUrlStartParam(["vk_ref", "start_param"]),
    getUser: () => user,
    getLanguage: () => {
      const language = getUrl()?.searchParams.get("vk_language");
      return language || browserLanguage();
    },
    showBackButton: noopBackButton,
    setClosingConfirmation: () => undefined,
    syncViewportCssVars: () => makeViewportSync(),
    openLink: fallbackOpenLink,
    share: async (data) => {
      if (window.vkBridge && data.url) {
        await send("VKWebAppShare", { link: data.url });
        return;
      }
      await fallbackShare(data);
    },
    haptic: (style = "light") => {
      void send("VKWebAppTapticImpactOccurred", { style }).catch(() => undefined);
    },
  };
}

function createInternationalAdapter(platform: InternationalHost): PlatformAdapter {
  return {
    platform,
    isEmbedded: true,
    hasNativeBackButton: false,
    capabilities: {
      ownsTopBackChrome: true,
      canRegisterBackHandler: false,
      providesViewport: false,
      showProductNavigation: true,
    },
    ready: async () => initializeInternationalHost(platform),
    expand: async () => undefined,
    // The auth proof is deliberately not exposed through PlatformAdapter.
    // host-auth obtains it from the official SDK only when the user continues.
    getLaunchData: () => null,
    getStartParam: () =>
      currentUrlStartParam(
        platform === "line"
          ? ["liff.state", "start_param"]
          : platform === "discord"
            ? ["custom_id", "start_param"]
            : ["context", "start_param"],
      ),
    getUser: () => {
      const hint = getInternationalHostHint(platform);
      return hint
        ? {
            id: hint.id,
            firstName: hint.firstName,
            lastName: hint.lastName,
            username: hint.username,
            photoUrl: hint.photoUrl,
          }
        : null;
    },
    getLanguage: () => getInternationalHostHint(platform)?.language ?? browserLanguage(),
    showBackButton: noopBackButton,
    setClosingConfirmation: () => undefined,
    syncViewportCssVars: () => makeViewportSync(),
    openLink: fallbackOpenLink,
    share: fallbackShare,
    haptic: () => {
      if (typeof navigator !== "undefined") navigator.vibrate?.(10);
    },
  };
}

let cachedPlatform: HostPlatform | null = null;
let cachedAdapter: PlatformAdapter | null = null;
let maxScriptPromise: Promise<void> | null = null;
let vkBridgePromise: Promise<VkBridge | undefined> | null = null;

/** Lazily imports the official VK Bridge only for a VK launch.
    window.vkBridge remains an injectable override for tests/host shells. */
export function ensureVkBridge(): Promise<VkBridge | undefined> {
  if (typeof window === "undefined") return Promise.resolve(undefined);
  if (!hasVkLaunchParams()) return Promise.resolve(window.vkBridge);
  if (window.vkBridge) return Promise.resolve(window.vkBridge);
  vkBridgePromise ??= import("@vkontakte/vk-bridge")
    .then((module) => {
      const bridge = module.default as unknown as VkBridge;
      window.vkBridge = bridge;
      return bridge;
    })
    .catch(() => undefined);
  return vkBridgePromise;
}

/** Loads MAX's official bridge only for a real MAX fragment launch. */
export function ensureMaxWebAppScript(): Promise<void> {
  if (typeof window === "undefined" || window.WebApp || !hasMaxLaunchParams()) {
    return Promise.resolve();
  }
  if (!maxScriptPromise) {
    maxScriptPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://st.max.ru/js/max-web-app.js";
      script.async = true;
      script.dataset.lexibitMaxSdk = "loading";
      script.onload = () => {
        script.dataset.lexibitMaxSdk = "loaded";
        resolve();
      };
      script.onerror = () => {
        script.remove();
        resolve();
      };
      document.head.appendChild(script);
    });
  }
  return maxScriptPromise;
}

export function getPlatformAdapter(): PlatformAdapter {
  const platform = detectPlatform();
  if (cachedAdapter && cachedPlatform === platform) return cachedAdapter;
  cachedPlatform = platform;
  cachedAdapter =
    platform === "telegram"
      ? createTelegramAdapter()
      : platform === "vk"
        ? createVkAdapter()
        : platform === "max"
          ? createMaxAdapter()
          : isInternationalHost(platform)
            ? createInternationalAdapter(platform)
          : createWebAdapter(platform === "unknown" ? "unknown" : "web");
  return cachedAdapter;
}

const FRESH_LAUNCH_MS = 15_000;

/** Waiting-worker lifecycle follows the same embedded-host decision used by
 * navigation and chrome. It must not grow its own query/global heuristics. */
export function shouldActivateWaitingWorkerAtLaunch(elapsedMs: number): boolean {
  return getPlatformAdapter().isEmbedded && elapsedMs < FRESH_LAUNCH_MS;
}

/** Test helper and SDK-late-load escape hatch. */
export function resetPlatformAdapter(): void {
  cachedPlatform = null;
  cachedAdapter = null;
  maxScriptPromise = null;
  vkBridgePromise = null;
}

/** Loads host SDK state where needed, then initializes host chrome. */
export async function initializePlatform(): Promise<PlatformAdapter> {
  const platform = detectPlatform();
  if (platform === "telegram") await ensureTelegramWebAppScript();
  if (platform === "max") await ensureMaxWebAppScript();
  if (isInternationalHost(platform)) await initializeInternationalHost(platform);
  resetPlatformAdapter();
  const adapter = getPlatformAdapter();
  await adapter.ready();
  await adapter.expand();
  syncBrowserViewport();
  return adapter;
}
